'use client'

import { AuthClient } from '@/auth/AuthClient'
import { InstantUserWelcomeDialog } from '@/components/InstantUserWelcomeDialog/InstantUserWelcomeDialog'
import { LogoFullSizable } from '@/components/Logo/LogoFullSizable'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { NavConfig } from '@/config/partials/navigation.config'
import { API_AUTH_BASE_URL } from '@/consts/baseUrls'
import { cn } from '@/lib/utils'
import axios from '@/utils/axios'
import { clearClientSessionId, flushEventBuffer } from '@/utils/eventLogging'
import { LogIn, LogOut, Menu } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { RegisterDialog } from 'src/components/Auth/RegisterDialog'

const Navigation = () => {
    const pathname = usePathname()
    const router = useRouter()
    const [user, setUser] = useState<{
        id: string
        role: 'USER' | 'ADMIN' | 'TEACHER_INSTANT' | 'GLOBAL_INSTANT' | 'EVENT_INSTANT'
    } | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

    const navItems = NavConfig.get()
    const isEventInstantUser = user?.role === 'EVENT_INSTANT'
    const isInstantUser = isEventInstantUser || user?.role === 'GLOBAL_INSTANT' || user?.role === 'TEACHER_INSTANT'

    useEffect(() => {
        let isMounted = true

        const loadSession = async () => {
            try {
                setIsLoading(true)
                const res = await axios.get(`${API_AUTH_BASE_URL}/session`, {
                    withCredentials: true,
                })
                if (isMounted) setUser(res.data?.user ?? null)
            } catch (_error) {
                if (isMounted) setUser(null)
            } finally {
                if (isMounted) setIsLoading(false)
            }
        }

        loadSession()

        return () => {
            isMounted = false
        }
    }, [pathname])

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false)
    }, [pathname])

    const handleLogout = async () => {
        flushEventBuffer()
        setIsMobileMenuOpen(false)
        await AuthClient.logout()
        clearClientSessionId()
        setUser(null)
        router.replace('/auth')
    }

    // Hide navigation completely on main auth page
    if (pathname === '/auth') {
        return null
    }

    // Auth-related pages that show logo-only navigation
    const logoOnlyPaths = ['/auth/', '/activate-parent', '/verify/confirmed', '/register', '/login']
    const isLogoOnlyPage = logoOnlyPaths.some((path) => pathname.startsWith(path))

    if (isLogoOnlyPage) {
        return (
            <nav className="fixed left-0 right-0 top-0 z-50 glass-card border-b border-border/50">
                <div className="container mx-auto px-4">
                    <div className="flex items-center h-16 md:h-20">
                        <Link href="/" className="flex items-center gap-2 justify-center">
                            <LogoFullSizable mb="" />
                        </Link>
                    </div>
                </div>
            </nav>
        )
    }

    return (
        <>
            {/* Top navigation bar - always at top */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16 md:h-20">
                        {/* Mobile: Burger menu button */}
                        <div className="flex md:hidden items-center">
                            <button
                                type="button"
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="flex items-center justify-center w-10 h-10 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300"
                                aria-label="Menü öffnen"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Desktop: Logo */}
                        <div className="hidden md:flex md:flex-1 items-center">
                            <Link href="/" className="flex items-center gap-2">
                                <LogoFullSizable mb="" />
                            </Link>
                        </div>

                        {/* Mobile: Logo centered */}
                        <div className="flex md:hidden items-center">
                            <Link href="/" className="flex items-center gap-2">
                                <LogoFullSizable mb="" />
                            </Link>
                        </div>

                        {/* Mobile: Spacer to balance the burger button */}
                        <div className="w-10 md:hidden" />

                        {/* Desktop: Nav Items */}
                        <div className="hidden md:flex items-center gap-2">
                            {navItems.main.map((item) => {
                                if (!item.visible) return null
                                const isActive = pathname.startsWith(item.path)
                                const Icon = item.icon

                                return (
                                    <Link
                                        key={item.path}
                                        href={item.path}
                                        className={cn(
                                            'flex flex-row items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300',
                                            isActive
                                                ? 'text-primary bg-primary/10'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                                        )}
                                    >
                                        <Icon className={cn('w-5 h-5', isActive && 'animate-pulse-glow')} />
                                        <span className="text-sm font-medium">{item.label}</span>
                                    </Link>
                                )
                            })}
                        </div>

                        {/* Desktop: Auth section */}
                        <div className="hidden md:flex md:flex-1 items-center justify-end gap-2">
                            {isLoading ? null : user ? (
                                <>
                                    {isEventInstantUser ? (
                                        <Button
                                            variant="hero"
                                            size="sm"
                                            className="animate-glow-ring"
                                            onClick={() => setIsRegisterDialogOpen(true)}
                                        >
                                            Account anlegen
                                        </Button>
                                    ) : null}
                                    <Button variant="ghost" size="sm" onClick={handleLogout}>
                                        <LogOut className="w-4 h-4" />
                                    </Button>
                                </>
                            ) : (
                                <Link href="/auth">
                                    <Button variant="hero" size="sm">
                                        <LogIn className="w-4 h-4 mr-1" />
                                        Login
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile: Sheet menu (slide from left) */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetContent side="left" className="w-72 glass-card border-r border-border/50 p-0">
                    <SheetTitle className="sr-only">Navigation</SheetTitle>
                    <div className="flex flex-col h-full">
                        {/* Sheet header with logo */}
                        <div className="flex items-center h-16 px-6 border-b border-border/50">
                            <Link href="/" onClick={() => setIsMobileMenuOpen(false)}>
                                <LogoFullSizable mb="" />
                            </Link>
                        </div>

                        {/* Nav items */}
                        <div className="flex flex-col gap-1 p-4 flex-1">
                            {navItems.main.map((item) => {
                                if (!item.visible) return null
                                const isActive = pathname.startsWith(item.path)
                                const Icon = item.icon

                                return (
                                    <Link
                                        key={item.path}
                                        href={item.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={cn(
                                            'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300',
                                            isActive
                                                ? 'text-primary bg-primary/10'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                                        )}
                                    >
                                        <Icon className={cn('w-5 h-5', isActive && 'animate-pulse-glow')} />
                                        <span className="text-sm font-medium">{item.label}</span>
                                    </Link>
                                )
                            })}

                            {/* Account anlegen for event instant users */}
                            {!isLoading && isEventInstantUser ? (
                                <Button
                                    variant="hero"
                                    size="sm"
                                    className="animate-glow-ring mt-2"
                                    onClick={() => {
                                        setIsMobileMenuOpen(false)
                                        setIsRegisterDialogOpen(true)
                                    }}
                                >
                                    Account anlegen
                                </Button>
                            ) : null}
                        </div>

                        {/* Bottom section: Logout */}
                        {!isLoading && user ? (
                            <div className="p-4 border-t border-border/50">
                                <button
                                    type="button"
                                    className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300"
                                    onClick={handleLogout}
                                >
                                    <LogOut className="w-5 h-5" />
                                    <span className="text-sm font-medium">Logout</span>
                                </button>
                            </div>
                        ) : null}

                        {!isLoading && !user ? (
                            <div className="p-4 border-t border-border/50">
                                <Link
                                    href="/auth"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-300"
                                >
                                    <LogIn className="w-5 h-5" />
                                    <span className="text-sm font-medium">Login</span>
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </SheetContent>
            </Sheet>

            <RegisterDialog
                open={isRegisterDialogOpen}
                onOpenChange={setIsRegisterDialogOpen}
                source="navigation"
                onSuccess={(payload) => {
                    if (payload.transferData) {
                        setUser((currentUser) => (currentUser ? { ...currentUser, role: 'USER' } : currentUser))
                    }
                }}
            />

            <InstantUserWelcomeDialog isInstantUser={isInstantUser} />
        </>
    )
}

export default Navigation
